import {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  WheelEvent as ReactWheelEvent,
} from "react";
import {
  faChevronDown,
  faChevronRight,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Loader } from "@fyrstain/hl7-front-library";
import { Badge, Button, Card, Form, InputGroup } from "react-bootstrap";
import {
  collectHierarchyKeys,
  countHierarchyLeaves,
  countHierarchyNodes,
  filterHierarchyTree,
  HierarchyNode,
  normalizeHierarchySearchValue,
} from "../../utils/hierarchyTree";
import "./HierarchyChartView.css";

type TransformState = {
  scale: number;
  x: number;
  y: number;
};

type PanState = {
  pointerId: number;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
} | null;

type HierarchyChartViewLabels = {
  searchPlaceholder: string;
  reset: string;
  center: string;
  expandAll: string;
  collapseAll: string;
  root: string;
  level: string;
  childItems: string;
  leaf: string;
  searchResults: string;
  structureEmpty: string;
  structureNoResults: string;
  open: string;
  close: string;
  details: string;
  totalItemsLabel: string;
  rootItemsLabel: string;
  leafItemsLabel: string;
};

type HierarchyChartViewProps<T> = {
  items: T[];
  loading?: boolean;
  searchTerm?: string;
  onSearchTermChange?: (value: string) => void;
  buildTree: (items: T[]) => HierarchyNode<T>[];
  getKey: (item: T) => string;
  getDisplayName: (item: T) => string;
  getSearchTerms: (item: T) => string[];
  onGoToDetails: (itemId?: string | null) => void;
  labels: HierarchyChartViewLabels;
};

const MIN_SCALE = 0.1;
const MAX_SCALE = 1.8;
const DEFAULT_SCALE = 1;
const SCALE_STEP = 0.15;
const ROOT_TOP_OFFSET = 48;

function HierarchyChartView<T extends object>({
  items,
  loading = false,
  searchTerm: providedSearchTerm,
  onSearchTermChange,
  buildTree,
  getKey,
  getDisplayName,
  getSearchTerms,
  onGoToDetails,
  labels,
}: HierarchyChartViewProps<T>) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const panStateRef = useRef<PanState>(null);
  const [collapsedNodes, setCollapsedNodes] = useState<Record<string, boolean>>({});
  const [internalSearchTerm, setInternalSearchTerm] = useState("");
  const [transform, setTransform] = useState<TransformState>({
    scale: DEFAULT_SCALE,
    x: 0,
    y: 0,
  });

  const searchTerm = providedSearchTerm ?? internalSearchTerm;
  const setSearchTerm = onSearchTermChange ?? setInternalSearchTerm;

  const tree = useMemo(() => buildTree(items), [buildTree, items]);
  const normalizedSearch = useMemo(
    () => normalizeHierarchySearchValue(searchTerm),
    [searchTerm]
  );
  const filteredTree = useMemo(
    () => filterHierarchyTree(tree, normalizedSearch, getKey, getDisplayName, getSearchTerms),
    [tree, normalizedSearch, getDisplayName, getKey, getSearchTerms]
  );
  const totalItems = items.length;
  const totalRoots = filteredTree.nodes.length;
  const totalLeaves = countHierarchyLeaves(filteredTree.nodes);
  const totalMatches = normalizedSearch
    ? countHierarchyNodes(filteredTree.nodes)
    : totalItems;
  const hasMultipleRoots = filteredTree.nodes.length > 1;
  const allTreeKeys = useMemo(
    () => collectHierarchyKeys(tree, getKey, getDisplayName),
    [tree, getDisplayName, getKey]
  );

  const toggleNode = useCallback((key: string) => {
    setCollapsedNodes((current) => ({
      ...current,
      [key]: !(current[key] ?? true),
    }));
  }, []);

  const clampScale = useCallback((value: number) => {
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));
  }, []);

  const zoomAtPoint = useCallback(
    (clientX: number, clientY: number, nextScale: number) => {
      const viewport = viewportRef.current;
      if (!viewport) {
        setTransform((current) => ({ ...current, scale: clampScale(nextScale) }));
        return;
      }

      const rect = viewport.getBoundingClientRect();
      const safeNextScale = clampScale(nextScale);
      setTransform((current) => {
        const pointX = clientX - rect.left;
        const pointY = clientY - rect.top;
        const scaleRatio = safeNextScale / current.scale;

        return {
          scale: safeNextScale,
          x: pointX - (pointX - current.x) * scaleRatio,
          y: pointY - (pointY - current.y) * scaleRatio,
        };
      });
    },
    [clampScale]
  );

  const zoomIn = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const rect = viewport.getBoundingClientRect();
    zoomAtPoint(rect.width / 2, rect.height / 2, transform.scale + SCALE_STEP);
  }, [transform.scale, zoomAtPoint]);

  const zoomOut = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const rect = viewport.getBoundingClientRect();
    zoomAtPoint(rect.width / 2, rect.height / 2, transform.scale - SCALE_STEP);
  }, [transform.scale, zoomAtPoint]);

  const resetView = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      setTransform({
        scale: DEFAULT_SCALE,
        x: 0,
        y: 0,
      });
      return;
    }

    const rootNode = viewport.querySelector<HTMLElement>('[data-hierarchy-root="true"]');
    if (!rootNode) {
      setTransform({
        scale: DEFAULT_SCALE,
        x: 0,
        y: 0,
      });
      return;
    }

    const viewportWidth = viewport.clientWidth;
    const rootCenterX = rootNode.offsetLeft + rootNode.offsetWidth / 2;
    const rootTopY = rootNode.offsetTop;

    setTransform({
      scale: DEFAULT_SCALE,
      x: viewportWidth / 2 - rootCenterX,
      y: ROOT_TOP_OFFSET - rootTopY,
    });
  }, []);

  const expandAll = useCallback(() => {
    setCollapsedNodes(
      allTreeKeys.reduce<Record<string, boolean>>((accumulator, key) => {
        accumulator[key] = false;
        return accumulator;
      }, {})
    );
  }, [allTreeKeys]);

  const collapseAll = useCallback(() => {
    setCollapsedNodes(
      allTreeKeys.reduce<Record<string, boolean>>((accumulator, key) => {
        accumulator[key] = true;
        return accumulator;
      }, {})
    );
  }, [allTreeKeys]);

  useEffect(() => {
    if (normalizedSearch) {
      setCollapsedNodes((current) => {
        const nextState = { ...current };

        filteredTree.expandedKeys.forEach((key) => {
          nextState[key] = false;
        });

        return nextState;
      });
    }
  }, [filteredTree.expandedKeys, normalizedSearch]);

  const handleWheel = useCallback(
    (event: ReactWheelEvent<HTMLDivElement>) => {
      event.preventDefault();
      const direction = event.deltaY < 0 ? 1 : -1;
      const nextScale = transform.scale + direction * SCALE_STEP;
      zoomAtPoint(event.clientX, event.clientY, nextScale);
    },
    [transform.scale, zoomAtPoint]
  );

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return;
      }

      panStateRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: transform.x,
        originY: transform.y,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [transform.x, transform.y]
  );

  const handlePointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const panState = panStateRef.current;
    if (!panState || panState.pointerId !== event.pointerId) {
      return;
    }

    setTransform((current) => ({
      ...current,
      x: panState.originX + (event.clientX - panState.startX),
      y: panState.originY + (event.clientY - panState.startY),
    }));
  }, []);

  const endPan = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (panStateRef.current?.pointerId === event.pointerId) {
      panStateRef.current = null;
    }
  }, []);

  function getVisibleSpan(node: HierarchyNode<T>): number {
    const key = getKey(node) || getDisplayName(node);
    const isSearchExpanded = filteredTree.expandedKeys.has(key);
    const isCollapsed = normalizedSearch
      ? !isSearchExpanded
      : collapsedNodes[key] ?? true;

    if (node.children.length === 0 || isCollapsed) {
      return 1;
    }

    return node.children.reduce((total, child) => total + getVisibleSpan(child), 0);
  }

  function renderNode(node: HierarchyNode<T>, depth: number): JSX.Element {
    const key = getKey(node) || getDisplayName(node);
    const isSearchExpanded = filteredTree.expandedKeys.has(key);
    const isSearchMatch = filteredTree.matchKeys.has(key);
    const isCollapsed = normalizedSearch
      ? (collapsedNodes[key] ?? !isSearchExpanded)
      : collapsedNodes[key] ?? true;
    const hasChildren = node.children.length > 0;
    const childCount = node.children.length;
    const visibleSpan = getVisibleSpan(node);
    const childSpans = node.children.map((child) => getVisibleSpan(child));
    const totalChildSpan = childSpans.reduce((total, span) => total + span, 0);
    const firstChildSpan = childSpans[0] ?? 1;
    const lastChildSpan = childSpans[childSpans.length - 1] ?? 1;

    return (
      <div
        className={`hierarchy-chart-node ${hasChildren ? "has-children" : "is-leaf"} ${
          hasChildren && !isCollapsed ? "is-expanded" : "is-collapsed"
        }`}
        style={
          {
            ["--org-node-span" as any]: visibleSpan,
          } as CSSProperties
        }
        data-hierarchy-root={depth === 0 ? "true" : undefined}
        key={`${key}-${depth}`}
      >
        <Card className={`hierarchy-chart-card ${isSearchMatch ? "is-search-match" : ""}`}>
          <Card.Body className="hierarchy-chart-card-body">
            <div className="hierarchy-chart-card-top">
              <div className="hierarchy-chart-title-block">
                <div className="hierarchy-chart-name">{getDisplayName(node)}</div>
              </div>
            </div>
            <div className="hierarchy-chart-card-footer">
              <Badge bg={hasChildren ? "primary" : "success"} className="hierarchy-chart-children-badge">
                {childCount} {labels.childItems}
              </Badge>
            </div>
            <div className="hierarchy-chart-card-actions">
              {hasChildren && (
                <button
                  type="button"
                  className="hierarchy-chart-toggle"
                  onClick={() => toggleNode(key)}
                  onPointerDown={(event) => event.stopPropagation()}
                  aria-label={isCollapsed ? labels.open : labels.close}
                >
                  <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronDown} />
                </button>
              )}
              <button
                type="button"
                className="hierarchy-chart-details"
                onClick={() => onGoToDetails((node as any)?.id ?? key)}
                onPointerDown={(event) => event.stopPropagation()}
                aria-label={labels.details}
              >
                <FontAwesomeIcon icon={faEye} />
              </button>
            </div>
          </Card.Body>
        </Card>

        {!isCollapsed && hasChildren && (
          <div
            className="hierarchy-chart-children"
            style={
              {
                ["--org-child-count" as any]: node.children.length,
                ["--org-child-span-total" as any]: totalChildSpan,
                ["--org-first-child-span" as any]: firstChildSpan,
                ["--org-last-child-span" as any]: lastChildSpan,
              } as CSSProperties
            }
          >
            {node.children.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="hierarchy-chart-toolbar">
        <div className="hierarchy-chart-search">
          <InputGroup>
            <Form.Control
              type="search"
              value={searchTerm}
              placeholder={labels.searchPlaceholder}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
            <Button
              variant="outline-secondary"
              onClick={() => setSearchTerm("")}
              disabled={!searchTerm}
            >
              {labels.reset}
            </Button>
          </InputGroup>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-3 mb-4">
        <Badge bg="secondary">
          {totalItems} {labels.totalItemsLabel}
        </Badge>
        <Badge bg="info">
          {totalRoots} {labels.rootItemsLabel}
        </Badge>
        <Badge bg="success">
          {totalLeaves} {labels.leafItemsLabel}
        </Badge>
        {normalizedSearch && (
          <Badge bg="primary">
            {totalMatches} {labels.searchResults}
          </Badge>
        )}
      </div>

      {loading ? (
        <div className="displayFlexCenter py-5">
          <Loader />
        </div>
      ) : filteredTree.nodes.length > 0 ? (
        <div
          ref={viewportRef}
          className="hierarchy-chart-viewport"
          onWheel={handleWheel}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endPan}
          onPointerLeave={endPan}
        >
          <div className="hierarchy-chart-actions">
            <Button
              size="sm"
              variant="outline-secondary"
              className="hierarchy-chart-action-button"
              onClick={resetView}
              onPointerDown={(event) => event.stopPropagation()}
            >
              {labels.center}
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              className="hierarchy-chart-action-button"
              onClick={expandAll}
              onPointerDown={(event) => event.stopPropagation()}
            >
              {labels.expandAll}
            </Button>
            <Button
              size="sm"
              variant="outline-secondary"
              className="hierarchy-chart-action-button"
              onClick={collapseAll}
              onPointerDown={(event) => event.stopPropagation()}
            >
              {labels.collapseAll}
            </Button>
            <div className="hierarchy-chart-zoom-pill" role="group" aria-label="Zoom">
              <Button
                size="sm"
                variant="outline-secondary"
                className="hierarchy-chart-action-button hierarchy-chart-zoom-button"
                onClick={zoomOut}
                onPointerDown={(event) => event.stopPropagation()}
              >
                -
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                className="hierarchy-chart-action-button hierarchy-chart-zoom-button"
                onClick={zoomIn}
                onPointerDown={(event) => event.stopPropagation()}
              >
                +
              </Button>
            </div>
          </div>
        <div
          className="hierarchy-chart-canvas"
          style={{
            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
          }}
        >
            <div
              className={`hierarchy-chart-tree ${
                hasMultipleRoots ? "has-multiple-roots" : ""
              }`}
            >
              {filteredTree.nodes.map((node) => renderNode(node, 0))}
            </div>
          </div>
        </div>
      ) : (
        <div>
          {normalizedSearch ? labels.structureNoResults : labels.structureEmpty}
        </div>
      )}
    </>
  );
}

export default HierarchyChartView;
